import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  ApplicationConfig,
  importProvidersFrom,
  makeEnvironmentProviders,
  provideBrowserGlobalErrorListeners,
} from '@angular/core';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { Title } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { provideServiceWorker } from '@angular/service-worker';
import { environment } from '@environments/environment';
import { Configuration as IdtApiConfiguration } from '@idt-api';
import { EffectsModule } from '@ngrx/effects';
import {
  FullRouterStateSerializer,
  routerReducer,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { MetaReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
  authLogoutMetaReducer,
  AuthGuard,
  AuthModule,
  OidcConfig,
  OidcConfigParameters,
} from '@pcscol-portail/ng-auth';
import {
  Config,
  ConfigModule,
  ConfigService,
  PcscolIconsModule,
} from '@pcscol-portail/composants-pegase';
import {
  Configuration as PiecesApiConfig,
  ConfigurationParameters as PiecesApiConfigParameters,
} from '@pieces-api';
import { AuthzConfig, AuthzConfigParameters, AuthzModule } from '@pcscol-portail/ng-authz';
import { routes } from './app.routes';
import { piecesJustificativesEffects } from './features/pieces-justificatives/store';
import { piecesJustificativesFeature } from './features/pieces-justificatives/store/pieces-justificatives.reducer';
import { appEffects } from './shared/store/app';
import { idtEffects } from './shared/store/idt';
import { idtFeature } from './shared/store/idt/idt.reducer';
import { navigationEffects } from './shared/store/navigation';
import { navigationFeature } from './shared/store/navigation/navigation.reducer';
import { titleFeature } from './shared/store/title/title.reducer';

export function authzConfig(configService: ConfigService) {
  return new AuthzConfig(configService.getConfig().authz as AuthzConfigParameters);
}
export function authConfig(configService: ConfigService) {
  return new OidcConfig(configService.getConfig().auth as OidcConfigParameters);
}

export function piecesApiConfig(configService: ConfigService) {
  return new PiecesApiConfig(configService.getConfig().api.pieces as PiecesApiConfigParameters);
}

export function idtApiConfig(configService: ConfigService) {
  return new IdtApiConfiguration(configService.getConfig().api.idt as IdtApiConfiguration);
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const metaReducers: MetaReducer<any>[] = [authLogoutMetaReducer];

export const appConfig: ApplicationConfig = {
  providers: [
    AuthGuard,
    provideHttpClient(withInterceptorsFromDi()),
    provideBrowserGlobalErrorListeners(),
    importProvidersFrom(
      PcscolIconsModule,
      StoreModule.forRoot(
        {
          router: routerReducer,
        },
        {
          metaReducers,
          runtimeChecks: {
            strictStateImmutability: false,
            strictActionImmutability: false,
          },
        },
      ),
      ConfigModule.forRoot(new Config({ location: '/assets/config/config.json' })),
      StoreModule.forFeature(titleFeature),
      StoreModule.forFeature(navigationFeature),
      StoreModule.forFeature(idtFeature),
      StoreModule.forFeature(piecesJustificativesFeature),
      EffectsModule.forRoot([
        navigationEffects,
        appEffects,
        idtEffects,
        piecesJustificativesEffects,
      ]),
      AuthModule.forRoot(),
      AuthzModule.forRoot(),
      StoreRouterConnectingModule.forRoot({
        serializer: FullRouterStateSerializer,
        stateKey: 'router',
      }),
      StoreDevtoolsModule.instrument({ connectInZone: true }),
      !environment.production ? StoreDevtoolsModule.instrument({ connectInZone: true }) : [],
    ),
    provideRouter(routes),
    // provideZonelessChangeDetection(),
    provideServiceWorker('ngsw-worker.js', {
      enabled: environment.production,
    }),
    makeEnvironmentProviders([
      { provide: OidcConfig, useFactory: authConfig, deps: [ConfigService] },
      { provide: AuthzConfig, useFactory: authzConfig, deps: [ConfigService] },
      { provide: PiecesApiConfig, useFactory: piecesApiConfig, deps: [ConfigService] },
      { provide: IdtApiConfiguration, useFactory: idtApiConfig, deps: [ConfigService] },
      { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { appearance: 'fill' } },
      Title,
    ]),
  ],
};
